import { type OAuth2Tokens } from '@docyrus/api-client';

import {
  type AppNavigationRequestMessage,
  type AppToHostMessage,
  type CalendarSendAckMessage,
  type ChatSendAckMessage,
  type DocyrusCalendarEventPayload,
  type DocyrusEmailPayload,
  type DocyrusNotification,
  type DocyrusSharePayload,
  type DocySendAckMessage,
  type EmailSendAckMessage,
  type GuidyCommandHandler,
  type GuidyCommandMessage,
  type HostNavigationHandler,
  type HostNavigationMessage,
  type HostNavigationRequestOptions,
  type HostNotificationHandler,
  type HostNotificationMessage,
  type HostSignInMessage,
  type HostToAppMessage,
  type MsGraphRequest,
  type MsGraphResponse,
  type MsGraphResponseMessage,
  type MsGraphStatus,
  type MsGraphStatusResponseMessage,
  type RouteChangePayload
} from '../types';

import {
  DEFAULT_ALLOWED_HOST_PATTERN,
  HOST_ACTION_TIMEOUT_MS,
  IFRAME_TOKEN_REFRESH_TIMEOUT_MS,
  MSGRAPH_REQUEST_TIMEOUT_MS
} from '../constants';
import { parseJwtExp } from './jwt';

type IframeAuthCallback = (tokens: OAuth2Tokens) => void;

/** Host → App acks for app-initiated fire-and-ack actions (Docy/Chat/Email/Calendar). */
type ActionAckMessage
  = DocySendAckMessage
    | ChatSendAckMessage
    | EmailSendAckMessage
    | CalendarSendAckMessage;

/**
 * Post a message to the host — works in both iframe and WebView contexts.
 * In a React Native WebView, window.ReactNativeWebView.postMessage is used.
 * In an iframe, window.parent.postMessage is used.
 *
 * If `window.__DOCYRUS_DEVTOOLS_MESSAGE_TAP__` is present (installed by
 * @docyrus/devtools), the outbound payload is reported to it so the
 * Messages panel can show app → host traffic alongside inbound messages.
 */
function postToHost(data: Record<string, unknown>): void {
  const rnwv = (window as any).ReactNativeWebView;
  const tap = (window as any).__DOCYRUS_DEVTOOLS_MESSAGE_TAP__;

  if (rnwv && typeof rnwv.postMessage === 'function') {
    if (typeof tap === 'function') tap('out', data, { transport: 'webview' });
    rnwv.postMessage(JSON.stringify(data));

    return;
  }

  if (typeof tap === 'function') tap('out', data, { transport: 'iframe', targetOrigin: '*' });
  window.parent.postMessage(data, '*');
}

/**
 * Iframe / WebView authentication via postMessage.
 *
 * Protocol:
 * - App → Host: { type: "signin-ready" }           (on start)
 * - Host → App: { type: "signin", accessToken, refreshToken }
 * - App → Host: { type: "token-refresh-request" }
 * - Host → App: { type: "navigation", url }
 * - Host → App: { type: "notification", notification }
 * - App → Host: { type: "route-change", path, search, hash, url }
 * - App → Host: { type: "navigation-request", url, replace?, newTab? }
 *
 * Every incoming message's event.origin is validated against
 * the allowed host pattern before processing.
 * In WebView mode, origin validation is skipped (ReactNativeWebView bridge).
 */
export class IframeAuth {
  private allowedOrigins: string[];
  private allowedPattern: RegExp;
  private isWebView: boolean;
  private onTokensReceived: IframeAuthCallback | null = null;
  private navigationHandlers: Set<HostNavigationHandler> = new Set();
  private notificationHandlers: Set<HostNotificationHandler> = new Set();
  private guidyHandlers: Set<GuidyCommandHandler> = new Set();
  private routeSyncEnabled = false;
  private originalPushState: typeof window.history.pushState | null = null;
  private originalReplaceState: typeof window.history.replaceState | null = null;
  private routeChangeListener: (() => void) | null = null;
  private stopped = false;
  private refreshPromise: {
    resolve: (tokens: OAuth2Tokens) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  } | null = null;
  private messageHandler: ((ev: MessageEvent) => void) | null = null;
  /**
   * In-flight app→host requests awaiting a correlated host reply, keyed by
   * requestId. Powers Send to Docy / Send to Chat / the MS Graph bridge.
   */
  private pendingRequests = new Map<string, {
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    timeoutId: ReturnType<typeof setTimeout>;
  }>();
  /** Fallback monotonic counter for request ids when crypto.randomUUID is absent. */
  private requestCounter = 0;
  constructor(
    allowedOrigins?: string[],
    allowedPattern?: RegExp
  ) {
    this.allowedOrigins = allowedOrigins ?? [];
    this.allowedPattern = allowedPattern ?? DEFAULT_ALLOWED_HOST_PATTERN;
    this.isWebView = typeof window !== 'undefined'
      && !!(window as any).ReactNativeWebView;
  }
  /**
   * Start listening for postMessage events from the host.
   * The callback is invoked every time valid tokens are received.
   */
  start(onTokensReceived: IframeAuthCallback): void {
    this.onTokensReceived = onTokensReceived;
    this.messageHandler = this.handleMessage.bind(this);
    window.addEventListener('message', this.messageHandler);

    postToHost({ type: 'signin-ready' });
  }
  /** Stop listening for postMessage events. */
  stop(): void {
    this.stopped = true;

    if (this.messageHandler) {
      window.removeEventListener('message', this.messageHandler);
      this.messageHandler = null;
    }
    this.rejectPendingRefresh('IframeAuth stopped');
    this.rejectAllPendingRequests('IframeAuth stopped');
    this.navigationHandlers.clear();
    this.notificationHandlers.clear();
    this.guidyHandlers.clear();
    this.teardownRouteSync();
  }
  /**
   * Patch history methods + listen for popstate/hashchange so every
   * route change posts a `route-change` message to the host.
   * Idempotent. Fires an immediate message with the current route.
   */
  enableRouteSync(): void {
    if (this.routeSyncEnabled || typeof window === 'undefined') return;
    this.routeSyncEnabled = true;

    const { history } = window;

    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<typeof history.pushState>) => {
      const result = this.originalPushState?.(...args);

      this.notifyRouteChange();

      return result;
    };

    history.replaceState = (...args: Parameters<typeof history.replaceState>) => {
      const result = this.originalReplaceState?.(...args);

      this.notifyRouteChange();

      return result;
    };

    this.routeChangeListener = () => this.notifyRouteChange();
    window.addEventListener('popstate', this.routeChangeListener);
    window.addEventListener('hashchange', this.routeChangeListener);

    // Report initial route so the host's address bar starts in sync
    this.notifyRouteChange();
  }
  /**
   * Post a single `route-change` message. With no argument, reads
   * `window.location`. Pass a payload from a router subscription to
   * report a known route instead.
   */
  notifyRouteChange(payload?: RouteChangePayload): void {
    if (this.stopped) return;

    let path: string;
    let search: string;
    let hash: string;

    if (payload) {
      path = payload.path ?? '';
      search = payload.search ?? '';
      hash = payload.hash ?? '';
    } else if (typeof window !== 'undefined') {
      ({ pathname: path, search, hash } = window.location);
    } else {
      return;
    }

    postToHost({
      type: 'route-change',
      path,
      search,
      hash,
      url: `${path}${search}${hash}`
    });
  }
  /**
   * Ask the host shell to navigate to a path/URL. The reverse of the
   * host → app `navigation` message: posts a `navigation-request` over the
   * same validated channel. The host decides how to honour it.
   */
  requestHostNavigation(url: string, options?: HostNavigationRequestOptions): void {
    if (this.stopped) return;
    if (typeof url !== 'string' || url.length === 0) return;

    const message: AppNavigationRequestMessage = { type: 'navigation-request', url };

    if (options?.replace) message.replace = true;

    if (options?.newTab) message.newTab = true;

    this.sendToHost(message);
  }
  private teardownRouteSync(): void {
    if (!this.routeSyncEnabled) return;

    if (typeof window !== 'undefined') {
      const { history } = window;

      if (this.originalPushState) history.pushState = this.originalPushState;

      if (this.originalReplaceState) history.replaceState = this.originalReplaceState;

      if (this.routeChangeListener) {
        window.removeEventListener('popstate', this.routeChangeListener);
        window.removeEventListener('hashchange', this.routeChangeListener);
      }
    }

    this.originalPushState = null;
    this.originalReplaceState = null;
    this.routeChangeListener = null;
    this.routeSyncEnabled = false;
  }
  /**
   * Subscribe to host navigation messages.
   * Returns an unsubscribe function.
   */
  onNavigation(handler: HostNavigationHandler): () => void {
    this.navigationHandlers.add(handler);

    return () => {
      this.navigationHandlers.delete(handler);
    };
  }
  /**
   * Subscribe to host notification messages.
   * Returns an unsubscribe function.
   */
  onNotification(handler: HostNotificationHandler): () => void {
    this.notificationHandlers.add(handler);

    return () => {
      this.notificationHandlers.delete(handler);
    };
  }
  /**
   * Subscribe to host `guidy:*` command messages (point/scan).
   * Returns an unsubscribe function. Used by the Guidy bridge runtime.
   */
  onGuidyCommand(handler: GuidyCommandHandler): () => void {
    this.guidyHandlers.add(handler);

    return () => {
      this.guidyHandlers.delete(handler);
    };
  }
  /**
   * Post an app → host message over the same validated channel used for
   * route-change and token-refresh. Used by the Guidy bridge to push its
   * element inventory and point acknowledgements.
   */
  sendToHost(message: AppToHostMessage): void {
    if (this.stopped) return;

    postToHost(message as unknown as Record<string, unknown>);
  }
  /**
   * Hand a payload to the host AI assistant (Docy). Resolves when the host
   * acknowledges delivery, rejects on host error or timeout.
   */
  sendToDocy(payload: DocyrusSharePayload): Promise<void> {
    const requestId = this.nextRequestId();

    return this.request<void>(
      { type: 'docy:send', requestId, payload },
      HOST_ACTION_TIMEOUT_MS
    );
  }
  /**
   * Hand a payload to the host team-chat app. Resolves when the host
   * acknowledges delivery, rejects on host error or timeout.
   */
  sendToChat(payload: DocyrusSharePayload): Promise<void> {
    const requestId = this.nextRequestId();

    return this.request<void>(
      { type: 'chat:send', requestId, payload },
      HOST_ACTION_TIMEOUT_MS
    );
  }
  /**
   * Hand a draft email to the host. Resolves when the host acknowledges,
   * rejects on host error or timeout.
   */
  sendToEmail(payload: DocyrusEmailPayload): Promise<void> {
    const requestId = this.nextRequestId();

    return this.request<void>(
      { type: 'email:send', requestId, payload },
      HOST_ACTION_TIMEOUT_MS
    );
  }
  /**
   * Hand a calendar event to the host. Resolves when the host acknowledges,
   * rejects on host error or timeout.
   */
  sendToCalendar(payload: DocyrusCalendarEventPayload): Promise<void> {
    const requestId = this.nextRequestId();

    return this.request<void>(
      { type: 'calendar:send', requestId, payload },
      HOST_ACTION_TIMEOUT_MS
    );
  }
  /** Ask the host whether its MSAL session is signed in. */
  requestMsGraphStatus(): Promise<MsGraphStatus> {
    const requestId = this.nextRequestId();

    return this.request<MsGraphStatus>(
      { type: 'msgraph:status-request', requestId },
      HOST_ACTION_TIMEOUT_MS
    );
  }
  /** Perform a Microsoft Graph request through the host's MSAL session. */
  requestMsGraph(request: MsGraphRequest): Promise<MsGraphResponse> {
    const requestId = this.nextRequestId();

    return this.request<MsGraphResponse>(
      { type: 'msgraph:request', requestId, request },
      MSGRAPH_REQUEST_TIMEOUT_MS
    );
  }
  /**
   * Post a correlated app→host request and await the matching host reply.
   * The reply is matched by `requestId` in `handleMessage`. Rejects on timeout
   * or when the bridge is stopped.
   */
  private request<T>(
    message: AppToHostMessage & { requestId: string },
    timeoutMs: number
  ): Promise<T> {
    if (this.stopped) {
      return Promise.reject(new Error('Cannot send host request: iframe bridge stopped'));
    }

    const { requestId } = message;

    return new Promise<T>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.pendingRequests.delete(requestId);
        reject(new Error(`Host request "${message.type}" timed out`));
      }, timeoutMs);

      this.pendingRequests.set(requestId, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId
      });

      this.sendToHost(message);
    });
  }
  /** Generate a correlation id, preferring crypto.randomUUID when available. */
  private nextRequestId(): string {
    const cryptoObj = typeof crypto !== 'undefined' ? crypto : undefined;

    if (cryptoObj && typeof cryptoObj.randomUUID === 'function') {
      return cryptoObj.randomUUID();
    }

    this.requestCounter += 1;

    return `req-${Date.now().toString(36)}-${this.requestCounter}-${Math.random().toString(36).slice(2, 10)}`;
  }
  private resolvePendingRequest(requestId: string, value: unknown): void {
    const pending = this.pendingRequests.get(requestId);

    if (!pending) return;

    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(requestId);
    pending.resolve(value);
  }
  private rejectPendingRequest(requestId: string, error: Error): void {
    const pending = this.pendingRequests.get(requestId);

    if (!pending) return;

    clearTimeout(pending.timeoutId);
    this.pendingRequests.delete(requestId);
    pending.reject(error);
  }
  private rejectAllPendingRequests(reason: string): void {
    for (const pending of this.pendingRequests.values()) {
      clearTimeout(pending.timeoutId);
      pending.reject(new Error(reason));
    }

    this.pendingRequests.clear();
  }
  /**
   * Request fresh tokens from the host.
   * Posts a "token-refresh-request" to the host, then waits for
   * a "signin" response.
   *
   * Only one refresh request can be in-flight at a time.
   * Additional callers share the same promise.
   */
  requestTokenRefresh(): Promise<OAuth2Tokens> {
    if (this.refreshPromise) {
      const existing = this.refreshPromise;

      return new Promise<OAuth2Tokens>((resolve, reject) => {
        const originalResolve = existing.resolve;
        const originalReject = existing.reject;

        existing.resolve = (tokens) => {
          originalResolve(tokens);
          resolve(tokens);
        };
        existing.reject = (error) => {
          originalReject(error);
          reject(error);
        };
      });
    }

    return new Promise<OAuth2Tokens>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        this.refreshPromise = null;
        reject(new Error('Token refresh request timed out'));
      }, IFRAME_TOKEN_REFRESH_TIMEOUT_MS);

      this.refreshPromise = { resolve, reject, timeoutId };

      postToHost({ type: 'token-refresh-request' });
    });
  }
  private isOriginAllowed(origin: string): boolean {
    // WebView bridge — origin validation not applicable
    if (this.isWebView) return true;

    if (this.allowedOrigins.includes(origin)) return true;

    return this.allowedPattern.test(origin);
  }
  private handleMessage(ev: MessageEvent): void {
    if (!this.isOriginAllowed(ev.origin)) return;

    const data = ev.data as HostToAppMessage;

    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;

    if (data.type === 'signin') {
      this.handleSignin(data as HostSignInMessage);

      return;
    }

    if (data.type === 'navigation') {
      this.handleNavigation(data as HostNavigationMessage);

      return;
    }

    if (data.type === 'notification') {
      this.handleNotification(data as HostNotificationMessage);

      return;
    }

    if (data.type === 'guidy:point' || data.type === 'guidy:scan') {
      this.handleGuidyCommand(data as GuidyCommandMessage);

      return;
    }

    if (
      data.type === 'docy:send-ack'
      || data.type === 'chat:send-ack'
      || data.type === 'email:send-ack'
      || data.type === 'calendar:send-ack'
    ) {
      this.handleActionAck(data as ActionAckMessage);

      return;
    }

    if (data.type === 'msgraph:status-response') {
      this.handleMsGraphStatusResponse(data as MsGraphStatusResponseMessage);

      return;
    }

    if (data.type === 'msgraph:response') {
      this.handleMsGraphResponse(data as MsGraphResponseMessage);
    }
  }
  private handleActionAck(message: ActionAckMessage): void {
    if (typeof message.requestId !== 'string') return;

    if (message.ok) {
      this.resolvePendingRequest(message.requestId, undefined);
    } else {
      this.rejectPendingRequest(
        message.requestId,
        new Error(message.error || `Host rejected ${message.type}`)
      );
    }
  }
  private handleMsGraphStatusResponse(message: MsGraphStatusResponseMessage): void {
    if (typeof message.requestId !== 'string') return;

    if (message.error) {
      this.rejectPendingRequest(message.requestId, new Error(message.error));

      return;
    }

    const status: MsGraphStatus = {
      signedIn: !!message.signedIn,
      account: message.account ?? null
    };

    this.resolvePendingRequest(message.requestId, status);
  }
  private handleMsGraphResponse(message: MsGraphResponseMessage): void {
    if (typeof message.requestId !== 'string') return;

    /*
     * A transport-level failure (host could not reach Graph at all) carries an
     * error and no status → reject. A completed Graph call — even a non-2xx —
     * resolves so the caller can inspect status/data.
     */
    if (message.error && typeof message.status !== 'number') {
      this.rejectPendingRequest(message.requestId, new Error(message.error));

      return;
    }

    const response: MsGraphResponse = {
      ok: !!message.ok,
      status: typeof message.status === 'number' ? message.status : 0,
      data: message.data,
      error: message.error
    };

    this.resolvePendingRequest(message.requestId, response);
  }
  private handleGuidyCommand(message: GuidyCommandMessage): void {
    for (const handler of this.guidyHandlers) {
      try {
        handler(message);
      } catch (err) {
        console.warn('[docyrus-signin] guidy command handler threw:', err);
      }
    }
  }
  private handleSignin(signinData: HostSignInMessage): void {
    if (!signinData.accessToken) return;

    /*
     * Derive expiry from the token's own exp claim. The host sends only the
     * raw tokens, so assuming a fixed 1-hour window made a long-lived iframe
     * session believe a near-expired token was still fresh — and ship it
     * expired to the backend (JWTExpired). Fall back to a 1-hour window only
     * when the token carries no readable exp.
     */
    const exp = parseJwtExp(signinData.accessToken);

    const tokens: OAuth2Tokens = {
      accessToken: signinData.accessToken,
      refreshToken: signinData.refreshToken,
      expiresAt: exp ?? Math.floor(Date.now() / 1000) + 3600,
      scope: ''
    };

    this.onTokensReceived?.(tokens);

    if (this.refreshPromise) {
      clearTimeout(this.refreshPromise.timeoutId);
      this.refreshPromise.resolve(tokens);
      this.refreshPromise = null;
    }
  }
  private handleNavigation(message: HostNavigationMessage): void {
    if (typeof message.url !== 'string' || message.url.length === 0) return;

    for (const handler of this.navigationHandlers) {
      try {
        handler(message);
      } catch (err) {
        console.warn('[docyrus-signin] host navigation handler threw:', err);
      }
    }
  }
  private handleNotification(message: HostNotificationMessage): void {
    const notification = message.notification as DocyrusNotification | undefined;

    if (!notification || typeof notification !== 'object' || typeof notification.id !== 'string') {
      return;
    }

    for (const handler of this.notificationHandlers) {
      try {
        handler(notification);
      } catch (err) {
        console.warn('[docyrus-signin] host notification handler threw:', err);
      }
    }
  }
  private rejectPendingRefresh(reason: string): void {
    if (this.refreshPromise) {
      clearTimeout(this.refreshPromise.timeoutId);
      this.refreshPromise.reject(new Error(reason));
      this.refreshPromise = null;
    }
  }
}
